import React, { useState } from 'react';
import SummarySection from '../SummarySection';
import Badge from '../Badge';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import axios from 'axios';

export default function MovieInfo({ data }) {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showResplitModal, setShowResplitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  const [feedbackData, setFeedbackData] = useState({
    feature_request: '' // Matching the Laravel controller field name
  });
  const [resplitData, setResplitData] = useState({
    splittingIssue: '',
    breakIntoTwo: '',
    midpoint: '',
    breakIntoThree: ''
  });

  const handleFeedbackChange = (e) => {
    setFeedbackData({
      ...feedbackData,
      [e.target.name]: e.target.value
    });
  };

  const handleResplitChange = (e) => {
    setResplitData({
      ...resplitData,
      [e.target.name]: e.target.value
    });
  };

  const handleFeedbackSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      // Get current URL 
      const currentUrl = window.location.href;
      
      // Add current URL to the feature request text itself
      const enhancedFeedbackData = {
        feature_request: `${feedbackData.feature_request}\n\nSubmitted from: ${currentUrl}`
      };
      
      // Using a new JSON API endpoint for feature requests
      const response = await axios.post('/feature-request/submit-api', enhancedFeedbackData, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        }
      });
      
      console.log('Feedback submitted successfully:', response.data);
      setSubmitSuccess(true);
      setTimeout(() => {
        setShowFeedbackModal(false);
        setSubmitSuccess(false);
        setFeedbackData({ feature_request: '' });
      }, 1500);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitError(error.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResplitSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      // Get current URL
      const currentUrl = window.location.href;
      
      // Include movie info and URL in the text of the splitting issue field
      const movieInfo = `Movie Title: ${data.title}\nMovie ID: ${data.id || 'N/A'}\nSubmitted from: ${currentUrl}`;
      const enhancedResplitData = {
        ...resplitData,
        splittingIssue: `${resplitData.splittingIssue}\n\n${movieInfo}`
      };
      
      // Submit to the JSON endpoint 
      const response = await axios.post('/api/resplit-act', enhancedResplitData, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        }
      });
      
      console.log('Resplit data submitted successfully:', response.data);
      setSubmitSuccess(true);
      setTimeout(() => {
        setShowResplitModal(false);
        setSubmitSuccess(false);
        setResplitData({
          splittingIssue: '',
          breakIntoTwo: '',
          midpoint: '',
          breakIntoThree: ''
        });
      }, 1500);
    } catch (error) {
      console.error('Error submitting resplit data:', error);
      setSubmitError(error.response?.data?.message || 'Failed to submit resplit data. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card shadow-sm p-4 mb-4">
      <div className="alert alert-warning mb-3" role="alert">
      <strong>Alpha Feature</strong> — This feature is in early development. The act and sequence splits will probably have issues.<br/>
        Some of the content will be inaccurate.<br/>We welcome your feedback!
      </div>
      
      <h3 className="card-title mb-3">{data.title}</h3>
      
      <div className="mb-4">
        <SummarySection title="Overall Score">
          <span className="fs-3 fw-bold text-primary">{data.average_pcr_score}</span>
        </SummarySection>
      </div>
      
      <p className="text-muted mb-2">Click on any act, section, or scene to explore it.</p>
      <p className="text-muted mb-4">Double-click on an act or sequence to zoom in. Double-click on the center to zoom out.</p>
      
      <div className="d-flex gap-2">
        <Button 
          variant="outline-primary" 
          onClick={() => setShowFeedbackModal(true)}
          className="me-2"
        >
          <i className="bi bi-chat-text me-2"></i>
          Send Feedback
        </Button>
        {/* <Button 
          variant="outline-secondary" 
          onClick={() => setShowResplitModal(true)}
        >
          <i className="bi bi-diagram-3 me-2"></i>
          Re-split Act
        </Button> */}
      </div>

      {/* Feedback Modal */}
      <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Send Feedback</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitSuccess ? (
            <div className="alert alert-success text-center">
              <i className="bi bi-check-circle me-2"></i>
              Feedback submitted successfully!
            </div>
          ) : (
            <Form>
              {submitError && (
                <div className="alert alert-danger mb-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {submitError}
                </div>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Comments, Suggestions, or Bug Reports</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="feature_request"
                  value={feedbackData.feature_request}
                  onChange={handleFeedbackChange}
                  placeholder="Share your thoughts, suggestions, or report any issues you've encountered..."
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFeedbackModal(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleFeedbackSubmit} 
            disabled={submitting || !feedbackData.feature_request.trim() || submitSuccess}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Submitting...
              </>
            ) : 'Submit Feedback'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Resplit Modal */}
      <Modal show={showResplitModal} onHide={() => setShowResplitModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Re-split Act</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitSuccess ? (
            <div className="alert alert-success text-center">
              <i className="bi bi-check-circle me-2"></i>
              Your act structure suggestions have been submitted successfully!
            </div>
          ) : (
            <Form>
              {submitError && (
                <div className="alert alert-danger mb-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {submitError}
                </div>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Why do you think the AI got the splitting wrong?</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="splittingIssue"
                  value={resplitData.splittingIssue}
                  onChange={handleResplitChange}
                  placeholder="Explain the issues with the current act splitting..."
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>What's the break into two moment in your script?</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="breakIntoTwo"
                  value={resplitData.breakIntoTwo}
                  onChange={handleResplitChange}
                  placeholder="Describe and/or give scene number..."
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>What's the midpoint of your script?</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="midpoint"
                  value={resplitData.midpoint}
                  onChange={handleResplitChange}
                  placeholder="Describe and/or give scene number..."
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>What's the break into three moment?</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="breakIntoThree"
                  value={resplitData.breakIntoThree}
                  onChange={handleResplitChange}
                  placeholder="Describe and/or give scene number..."
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResplitModal(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleResplitSubmit} 
            disabled={submitting || !resplitData.splittingIssue.trim() || submitSuccess}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Submitting...
              </>
            ) : 'Submit New Structure'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

MovieInfo.propTypes = {
  data: PropTypes.object.isRequired
};